import { stringify as qsify } from 'query-string';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { fireStructuredSnowplowEvent } from 'wanderu-service-analytics';
import { WapiService } from 'wanderu-service-wapi';
import {
    memoizeDefaultSerialize as defaultSerialize,
    formatAddressValue,
    memoize,
} from 'wanderu-utils';

import {
    OK,
    OVER_QUERY_LIMIT,
    MAX_TRIES,
    TIMEOUT_INTERVAL,
    ZERO_RESULTS,
} from './Constants';
import {
    NoResultsError,
    OverQueryLimit,
} from './errors';
import {
    filterUSAPredictions,
} from './helpers';

const logger = Logger.getLogger('GoogleService');

// GoogleService is a module-level singleton, so on the server its caches live
// as long as the process and fill with every place any request asked about;
// each cached place-details promise holds a whole Google response (#1231).
// The server keeps a few hundred entries: enough to skip the WAPI round trip
// for popular places, bounded however many distinct places are rendered.
const SERVER_CACHE_LIMIT = 500;

const CACHE_LIMITS = {
    client: {
        pictureByPlaceId: 10000,
        placeDetails: 5000,
        placePredictions: 10000,
    },
    server: {
        pictureByPlaceId: SERVER_CACHE_LIMIT,
        placeDetails: SERVER_CACHE_LIMIT,
        placePredictions: SERVER_CACHE_LIMIT,
    },
};

function serializeWithoutSessionToken(input, options = {}) {
    const {
        sessiontoken: _sessiontoken,
        meta: _meta,
        ...cleanedOptions
    } = options;

    return defaultSerialize(input, cleanedOptions);
}

class GoogleService {
    // The singleton is built at import time, before wanderu-config's env is
    // set, so the default checks for a browser directly.
    constructor({ isClient = typeof window !== 'undefined' } = {}) {
        const limits = isClient
            ? CACHE_LIMITS.client
            : CACHE_LIMITS.server;

        this.getPlaceDetails = memoize(this.getPlaceDetails.bind(this), {
            limit: limits.placeDetails,
            serializer: serializeWithoutSessionToken,
        });
        this.getPlacePredictions = memoize(this.getPlacePredictions.bind(this), {
            limit: limits.placePredictions,
            serializer: serializeWithoutSessionToken,
        });
        this.getPictureByPlaceId = memoize(this.getPictureByPlaceId.bind(this), {
            limit: limits.pictureByPlaceId,
        });
    }

    getPlacePredictions = async (text, options = {}) => {
        const searchText = text.toLowerCase();

        try {
            const predictions = await this.tryMultipleTimes(
                () => this.fetchPlacePredictions(searchText, options),
            );
            const sortedResults = predictions.sort(GoogleService.sortByLocality);
            return GoogleService.formatPredictions(sortedResults);
        } catch (ex) {
            if (ex instanceof NoResultsError) {
                fireStructuredSnowplowEvent({
                    action: 'submitted',
                    category: 'getPlacePredictions',
                    label: 'error',
                    property: JSON.stringify({
                        ...ex,
                        searchText,
                    }),
                });
            } else {
                logger.error('getPlacePredictions', ex);
            }

            throw ex;
        }
    };

    getPlaceDetails = async (placeId, options = {}) => {
        try {
            return this.tryMultipleTimes(() => this.fetchPlaceDetails(placeId, options));
        } catch (ex) {
            logger.error('getPlaceDetails', ex);
            throw ex;
        }
    };

    getPlacePredictionsForCities = async (searchText, options = {}) => {
        return this.getPlacePredictions(searchText.toLowerCase(), {
            ...options,
            types: '(cities)',
        });
    };

    getPlacePredictionsForRegions = async (searchText, options = {}) => {
        return this.getPlacePredictions(searchText.toLowerCase(), {
            ...options,
            types: '(regions)',
        });
    };

    fetchPlacePredictions = async (searchText, options = {}) => {
        const {
            locale = 'en-us',
            meta = {},
            v2 = false,
            ...nonCachableOptions
        } = options;

        const queryParams = {
            ...meta,
            ...nonCachableOptions,
        };

        const baseUrl = v2
            ? 'maps/predictions3'
            : 'maps/predictions';
        const url = `${baseUrl}/${locale}/${encodeURIComponent(searchText.toLowerCase())}/`;
        const response = await WapiService.getWithoutAuth(`${url}?${qsify(queryParams)}`);
        const result = response.result;

        if (result.status === OK) {
            return filterUSAPredictions(result.predictions);
        }

        if (result.status === ZERO_RESULTS) {
            throw new NoResultsError();
        }

        if (result.status === OVER_QUERY_LIMIT) {
            throw new OverQueryLimit();
        }

        throw new Error('An unknown Google Proxy error occurred.');
    }

    fetchPlaceDetails = async (placeId, options = {}) => {
        const {
            locale = 'en-us',
            ...nonCachableOptions
        } = options;

        const queryParams = {
            ...nonCachableOptions,
        };

        const url = `maps/places/${locale}/google/${placeId}/`;
        const response = await WapiService.getWithoutAuth(`${url}?${qsify(queryParams)}`);

        if (response.error) {
            throw new Error('An unknown Google Proxy error occurred.');
        }

        return response.result;
    }

    async getPictureByPlaceId(placeId, options = {}) {
        const place = await this.getPlaceDetails(placeId);

        if (!place.photos || !place.photos.length) {
            return undefined;
        }

        const qs = {
            ...options,
            // This needs to be the client key as the client makes the img request
            key: appSettings.config.google.apiKey,
            photoreference: place.photos[0].photo_reference,
        };

        return WapiService.buildUrl(`maps/photos?${qsify(qs)}`);
    }

    static formatPredictions(predictions) {
        return predictions
            .slice(0, 5)
            .map((prediction) => {
                return {
                    id: prediction?.place_id,
                    name: formatAddressValue(prediction?.description),
                    placeId: prediction?.place_id,
                    text: prediction?.description,
                };
            });
    }

    static sortByLocality(placeA, placeB) {
        if (placeA.types.includes('locality') === placeB.types.includes('locality')) {
            return 0;
        }

        if (placeA.types.includes('locality')) {
            return -1;
        }

        return 1;
    }

    async tryMultipleTimes(fn) {
        for (let i = 0; i < MAX_TRIES; i += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                return await fn();
            } catch (ex) {
                if (ex instanceof NoResultsError) {
                    throw ex;
                }
                // eslint-disable-next-line no-await-in-loop
                await this.wait(TIMEOUT_INTERVAL * (i + 1));
            }
        }

        throw new OverQueryLimit();
    }

    wait = async (ms) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, ms);
        });
    }
}

export {
    GoogleService,
    SERVER_CACHE_LIMIT,
};
export default new GoogleService();
